import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { LanguageToggle } from "@/i18n";
import { Package, Menu, X, LayoutDashboard, LogOut, Truck, Shield } from "lucide-react";

const NAV = [
  { to: "/anfrage", label: "Transport finden" },
  { to: "/partner", label: "Laderaum anbieten" },
  { to: "/aktuelle-beiladungen", label: "Aktuelle Beiladungen" },
  { to: "/volumenrechner", label: "Volumenrechner" },
];

const PAGE_TITLES = {
  "/": "", "/aktuelle-beiladungen": "Aktuelle Beiladungen", "/volumenrechner": "Volumenrechner", "/anfrage": "Anfrage stellen",
  "/angebote": "Angebote", "/checkout": "Buchung", "/login": "Anmelden", "/registrieren": "Registrieren",
  "/dashboard": "Mein Bereich", "/partner": "Partnerportal", "/admin": "Adminbereich", "/rechtliches": "Rechtliches",
};

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
      <div className="relative">
        <div className="absolute inset-0 bg-[#FF5A1F] blur-md opacity-40 group-hover:opacity-70 transition-opacity rounded-lg" />
        <div className="relative w-9 h-9 rounded-lg bg-[#FF5A1F] flex items-center justify-center">
          <Package className="w-5 h-5 text-white" strokeWidth={2.4} />
        </div>
      </div>
      <div className="leading-none">
        <div className="font-head font-extrabold text-[15px] tracking-tight text-white">EASY BEILADUNG</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF5A1F] font-semibold">Laderaum-Marktplatz</div>
      </div>
    </Link>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    const t = PAGE_TITLES[loc.pathname] || PAGE_TITLES[Object.keys(PAGE_TITLES).find((k) => k !== "/" && loc.pathname.startsWith(k))];
    document.title = t ? `${t} | Easy Beiladung` : "Easy Beiladung – Freier Laderaum trifft Transport";
  }, [loc.pathname]);

  const dashLink = user?.role === "admin" ? "/admin"
    : user?.role === "transport_partner" ? "/partner" : "/dashboard";

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="fixed top-0 inset-x-0 z-50 bg-[#0B0F17]/85 backdrop-blur-md border-b border-slate-800/80">
        <div className="eb-container flex items-center justify-between h-16">
          <Brand />
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.filter((n) => !(user?.role === "admin" && n.to === "/partner")).map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  loc.pathname === n.to ? "text-white bg-white/5" : "text-slate-300 hover:text-white hover:bg-white/5"}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            <LanguageToggle />
            {user ? (
              <>
                <Link to={dashLink} className="eb-btn-ghost" data-testid="nav-dashboard">
                  <LayoutDashboard className="w-4 h-4" /> {user.name?.split(" ")[0] || "Konto"}
                </Link>
                <button onClick={() => { logout(); nav("/"); }} className="eb-btn-outline !py-2 !px-3" data-testid="nav-logout">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="eb-btn-ghost" data-testid="nav-login">Anmelden</Link>
                <Link to="/anfrage" className="eb-btn-primary !py-2.5" data-testid="nav-cta-anfrage">Anfrage stellen</Link>
              </>
            )}
          </div>
          <div className="lg:hidden flex items-center gap-2">
            <LanguageToggle />
            <button className="text-slate-200" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-800 bg-[#0B0F17] px-4 py-4 space-y-1">
            {NAV.filter((n) => !(user?.role === "admin" && n.to === "/partner")).map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="block px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-white/5">{n.label}</Link>
            ))}
            <div className="pt-2 flex gap-2">
              {user ? (
                <>
                  <Link to={dashLink} onClick={() => setOpen(false)} className="eb-btn-outline flex-1">Konto</Link>
                  <button onClick={() => { logout(); nav("/"); setOpen(false); }} className="eb-btn-ghost">Abmelden</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="eb-btn-outline flex-1">Anmelden</Link>
                  <Link to="/anfrage" onClick={() => setOpen(false)} className="eb-btn-primary flex-1">Anfrage</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-16">
        <Outlet />
      </main>

      <footer className="border-t border-slate-800/80 bg-[#0B0F17] mt-auto">
        <div className="eb-container py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <Brand />
            <p className="text-sm text-slate-400 mt-4 max-w-xs">Beiladung einfach buchen. Nutze freien Laderaum auf bestehenden Transporten – deutschlandweit.</p>
          </div>
          <div>
            <div className="eb-label mb-3">Plattform</div>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/anfrage" className="hover:text-white">Transport finden</Link></li>
              <li><Link to="/partner" className="hover:text-white">Laderaum anbieten</Link></li>
              <li><Link to="/aktuelle-beiladungen" className="hover:text-white">Aktuelle Beiladungen</Link></li>
              <li><Link to="/volumenrechner" className="hover:text-white">Volumenrechner</Link></li>
            </ul>
          </div>
          <div>
            <div className="eb-label mb-3">Vertrauen</div>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#FF5A1F]" /> Professionelle Partner</li>
              <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#FF5A1F]" /> Versicherte Transporte</li>
            </ul>
          </div>
          <div>
            <div className="eb-label mb-3">Rechtliches</div>
            <ul className="space-y-2 text-sm text-slate-400">
              {[["impressum","Impressum"],["datenschutz","Datenschutz"],["agb","AGB"],["widerruf","Widerrufsbelehrung"],["kontakt","Kontakt"],["partnerbedingungen","Partnerbedingungen"]].map(([s,l])=>(
                <li key={s}><Link to={`/rechtliches/${s}`} className="hover:text-white">{l}</Link></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800/80">
          <div className="eb-container py-5 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} Easy Beiladung · easybeiladung.de</span>
            <span>Vermittlungsplattform für Beiladungen & freien Laderaum</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
